/**
 * Theme catalog and DOM/persistence helpers.
 *
 * Pure module (no React) so the synchronous pre-React bootstrap in `main.tsx`
 * can import `applyTheme` / `readPersistedTheme` and paint the right colors
 * before the first frame. The React side (`theme-context.tsx`) layers state and
 * session sync on top.
 */

import { configuredDocsOrigin } from "@/lib/docs-origin";

/** Preview swatch colors shown on the selector cards. */
interface ThemeSwatch {
  bg: string;
  fg: string;
  muted: string;
  accent: string;
}

export interface ThemeDef {
  id: Theme;
  label: string;
  description: string;
  swatch: ThemeSwatch;
}

export type Theme = "light" | "dark";

/** The two selectable themes, in display order. */
export const THEMES: readonly ThemeDef[] = [
  {
    id: "light",
    label: "Light",
    description: "Solarized claro — el clásico diurno.",
    swatch: { bg: "#ffffff", fg: "#142c4a", muted: "#536d8f", accent: "#037fb9" },
  },
  {
    id: "dark",
    label: "Dark",
    description: "Navy profundo — el oscuro de siempre.",
    swatch: { bg: "#0d1e30", fg: "#f5f5f5", muted: "#9ebcc0", accent: "#037fb9" },
  },
];

export const DEFAULT_THEME: Theme = "dark";

/** Whether the page runs on the public docs origin (the public portal). */
export function isPublicDocsOrigin(): boolean {
  return configuredDocsOrigin() === window.location.origin;
}

/**
 * The theme a reader with no persisted choice starts in. Only on the public
 * docs origin it follows the system (`prefers-color-scheme`), and is Light when
 * the system states no preference or cannot be asked; everywhere else it is the
 * application default.
 */
function initialTheme(): Theme {
  if (!isPublicDocsOrigin()) return DEFAULT_THEME;
  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
}

const THEME_IDS: ReadonlySet<string> = new Set(THEMES.map((t) => t.id));

export const THEME_STORAGE_KEY = "theme";

/**
 * The public docs origin's own key, written only when the reader selects a
 * theme. The application's `theme` key is written on every mount, so on that
 * origin it only records what was shown, never what the reader chose.
 */
export const DOCS_THEME_STORAGE_KEY = "docs-theme";

export function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && THEME_IDS.has(value);
}

/** Read old preferences without admitting retired values as active themes. */
export function normalizePersistedTheme(value: unknown): Theme | undefined {
  if (isTheme(value)) return value;
  if (value === "mono" || value === "midnight") return "dark";
  return undefined;
}

/** Whether the theme carries the legacy `.dark` class. */
export function isDark(theme: Theme): boolean {
  return theme === "dark";
}

/**
 * Apply a theme to the document element: set `data-theme` (palette source of
 * truth) and toggle the legacy `.dark` class for navy so existing
 * consumers (Mermaid theme, white-logo swap) keep working.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.classList.toggle("dark", isDark(theme));
}

/**
 * Resolve the persisted theme without React. On the public docs origin only the
 * reader's own selection (`docs-theme`) counts, then the system's theme; the
 * application's `theme` key and the account metadata are ignored there. Elsewhere
 * the order is: `localStorage` cache →
 * Supabase session cached in `localStorage` (`user_metadata.theme`) → the initial
 * theme (the system's on the public docs origin, else the default).
 * Seeds the `localStorage` cache from session metadata so later loads
 * short-circuit on the first branch.
 */
export function readPersistedTheme(): Theme {
  if (isPublicDocsOrigin()) {
    return normalizePersistedTheme(localStorage.getItem(DOCS_THEME_STORAGE_KEY)) ?? initialTheme();
  }
  const cached = localStorage.getItem(THEME_STORAGE_KEY);
  const cachedTheme = normalizePersistedTheme(cached);
  if (cachedTheme) {
    if (cachedTheme !== cached) localStorage.setItem(THEME_STORAGE_KEY, cachedTheme);
    return cachedTheme;
  }

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const meta = (JSON.parse(raw) as { user?: { user_metadata?: { theme?: unknown } } })?.user
          ?.user_metadata?.theme;
        const metaTheme = normalizePersistedTheme(meta);
        if (metaTheme) {
          localStorage.setItem(THEME_STORAGE_KEY, metaTheme);
          return metaTheme;
        }
      }
    } catch {
      // Ignore unparsable entries; fall through to default.
    }
    break;
  }

  return initialTheme();
}
